import {
	ChangeEventHandler,
	FC,
	useEffect,
	useLayoutEffect,
	useMemo,
	useState,
} from 'react';
import { SimpleGrid, Text, Accordion, Badge, TextInput } from '@mantine/core';
import { useTypedDispatch, useTypedSelector } from '@/store/hooks';
import { creatorActions } from '@/store/slices/creatorSlice';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Loading } from '@/components/ui/loading/Loading';
import { UUID } from '@/types/types';
import { pick } from 'lodash';
import { AccordionOptionCard } from '@/components/ui/AccordionOptionCard';
import { Presets } from '../../partials/presets/Presets';
import { getFileSrc } from '@/utils/utilities';
import { useStartCollectionMutation } from '@/hooks/useStartCollectionMutation';
import { useContent } from '@/providers/ContentProvider';
import PresetApi, { PresetStatus } from '@/api/PresetApi';
import ProductModuleCollectionApi from '@/api/ProductModuleCollectionApi';
import ProjectApi from '@/api/ProjectApi';
import APP_CONFIG from '@/configs/appConfig';

const baseSlug = 'collections.';

export const Collections: FC = () => {
	const dispatch = useTypedDispatch();
	const queryClient = useQueryClient();

	const { getContent } = useContent();

	const { selectedProductModuleCollection, project, preset, options } =
		useTypedSelector((app) => app.creator);

	const startCollectionMutation = useStartCollectionMutation();

	const productModuleCollectionsQuery = useQuery({
		queryKey: [
			ProductModuleCollectionApi.queryKey,
			{
				sortBy: {
					key: 'listOrder',
					order: 'ASC',
				},
			},
		],
		queryFn: ProductModuleCollectionApi.getAll,
	});

	useEffect(() => {
		if (!productModuleCollectionsQuery.data) return;

		for (const collection of productModuleCollectionsQuery.data) {
			queryClient.prefetchQuery({
				queryKey: [
					PresetApi.queryKey,
					{
						sortBy: {
							key: 'listOrder',
							order: 'ASC',
						},
					},
					{
						'filter.productModuleCollection.id': collection.id,
					},
				],
				queryFn: PresetApi.getAll,
			});
		}
	}, [productModuleCollectionsQuery.data]);

	const createProjectMutation = useMutation({
		mutationFn: ProjectApi.create,
		onSuccess: (project) => {
			dispatch(
				creatorActions.updateProject(
					pick(project, ['id', 'createdAt', 'updatedAt'])
				)
			);
		},
	});

	const createPresetMutation = useMutation({
		mutationFn: PresetApi.create,
		onSuccess: (preset) => {
			dispatch(
				creatorActions.updatePreset(
					pick(preset, ['id', 'createdAt', 'updatedAt'])
				)
			);
		},
	});

	const clearProjectMutation = useMutation({
		mutationFn: ProjectApi.clear,
		onSuccess: () => {
			dispatch(
				creatorActions.updateProject({ price: 0, creatorProductModules: [] })
			);
		},
	});

	const clearPresetMutation = useMutation({
		mutationFn: PresetApi.clear,
		onSuccess: () => {
			dispatch(
				creatorActions.updatePreset({ price: 0, creatorProductModules: [] })
			);
		},
	});

	const handleSelectCollection = (collectionId: UUID) => () => {
		if (options.isPresetMode) {
			if (!preset.id)
				createPresetMutation.mutate(
					{
						name: getContent(`${baseSlug}newPresetName`) as string,
						creatorProductModules: [],
						status: PresetStatus.INACTIVE,
						storeConfigurations: [],
					},
					{
						onSuccess: () => {
							startCollectionMutation.mutate(collectionId);
						},
					}
				);
			else
				clearPresetMutation.mutate(preset.id, {
					onSuccess: () => {
						startCollectionMutation.mutate(collectionId);
					},
				});
			return;
		}

		if (!project.id)
			createProjectMutation.mutate(
				{
					creatorProductModules: [],
				},
				{
					onSuccess: () => {
						startCollectionMutation.mutate(collectionId);
					},
				}
			);
		else
			clearProjectMutation.mutate(project.id, {
				onSuccess: () => {
					startCollectionMutation.mutate(collectionId);
				},
			});
	};

	useLayoutEffect(() => {
		if (!options.lockedCollectionId) return;

		handleSelectCollection(options.lockedCollectionId)();
	}, [options.lockedCollectionId]);

	const [searchFilter, setSearchFilter] = useState('');
	const handleChangeSearch: ChangeEventHandler<HTMLInputElement> = ({
		target,
	}) => {
		setSearchFilter(target.value);
	};

	const isFilterable =
		(productModuleCollectionsQuery.data?.length || 0) >
		APP_CONFIG.FILTERABLE_ATTRIBUTE_VALUES_THRESHOLD;

	const filteredCollections = useMemo(() => {
		return productModuleCollectionsQuery.data?.filter((collection) => {
			if (!searchFilter) return true;
			if (!isFilterable) return true;

			return collection.name
				.toLowerCase()
				.includes(searchFilter.toLowerCase().trim());
		});
	}, [productModuleCollectionsQuery.data, searchFilter]);

	if (!filteredCollections) return <Loading />;

	return (
		<Accordion mt="lg" defaultValue="collections" variant="">
			{!options.lockedCollectionId && (
				<Accordion.Item value="collections">
					<Accordion.Control
						icon={
							!!selectedProductModuleCollection && (
								<Badge>
									<Text size="sm">{selectedProductModuleCollection.name}</Text>
								</Badge>
							)
						}
					>
						{getContent(`${baseSlug}title`)}
					</Accordion.Control>
					<Accordion.Panel>
						{isFilterable && !options.isPreviewMode && (
							<TextInput
								placeholder={
									getContent(`${baseSlug}searchPlaceholder`) as string
								}
								className="flex-grow-1"
								mt="sm"
								value={searchFilter}
								onChange={handleChangeSearch}
							/>
						)}
						<SimpleGrid
							py="md"
							cols={{
								base: 2,
								sm: 3,
							}}
							spacing="xs"
						>
							{!options.isPreviewMode &&
								filteredCollections.map((collection) => {
									const isSelected =
										selectedProductModuleCollection?.id === collection.id;

									return (
										<AccordionOptionCard
											key={collection.id}
											id={collection.id}
											isSelected={isSelected}
											name={collection.name}
											description={collection.storeConfiguration.description}
											imagePath={getFileSrc(collection?.image?.path)}
											onSelect={handleSelectCollection(collection.id)}
											shopUrl={collection.storeConfiguration.shopUrl}
										/>
									);
								})}
							{options.isPreviewMode && !!selectedProductModuleCollection && (
								<AccordionOptionCard
									key={selectedProductModuleCollection.id}
									id={selectedProductModuleCollection.id}
									isSelected={true}
									name={selectedProductModuleCollection.name}
									description={
										selectedProductModuleCollection.storeConfiguration
											.description
									}
									imagePath={getFileSrc(
										selectedProductModuleCollection?.image?.path
									)}
									onSelect={() => {}}
									shopUrl={
										selectedProductModuleCollection.storeConfiguration.shopUrl
									}
								/>
							)}
						</SimpleGrid>
					</Accordion.Panel>
				</Accordion.Item>
			)}
			<Presets />
		</Accordion>
	);
};
